import { useQuery } from '@tanstack/react-query';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import Transaction from '@/models/Transaction';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { CommissionRules } from '@/models/CommissionRules';
import Debit from '@/models/Debit';
import { getRelativeDate } from '@/helpers/dateHelper';
import { FUTURE_WINDOW_DAYS } from './futureTransactions';

const FIVE_MINUTES = 5 * 60 * 1000;

/*
 * The support-requests range filter selects whole requests by their *first* month, so a
 * monthly support that started a year ago and has a month due this week only comes back
 * when the range reaches that far back. The window itself is applied client-side.
 */
const SUPPORT_RANGE_START = '2015-01-01';
const SUPPORT_RANGE_DAYS_AHEAD = 400;

export type FutureTransactionSource = 'aptTransactions' | 'supportRequests' | 'commissionRules' | 'bankStandingOrders';

export interface FutureTransactionSources {
  /** Movement details the accounting system already holds with a value date ahead of today. */
  aptTransactions: Transaction[] | undefined;
  supportRequests: SupportRequestDtoIn[] | undefined;
  /** What a support month and a deposit cost the fund, for the shortfall. */
  commissionRules: CommissionRules | undefined;
  /** The fund's standing orders as the accounting system holds them; undefined until read, or while the bank rows are switched off. */
  bankStandingOrders: Debit[] | undefined;
  isLoading: boolean;
  /** Sources that failed, so the group is missing rows it should have — the board names them. */
  failedSources: FutureTransactionSource[];
  /** Asks the failed sources again — the «נסה שוב» beside the failure line. */
  retryFailed: () => void;
}

/**
 * HAT-1203: the reads «תנועות עתידיות» needs beside the board's own transactions call.
 *
 * The post-dated accounting rows come from a second, small transactions call rather than by
 * stretching the main range: the server chains the running balance from the first row it
 * returns, and a future first row would have shifted every settled row's cumulative figure.
 * The commission rules are what turns a sum of supports into what is really missing.
 *
 * All are cached for five minutes rather than useGetAuthApi's zero, on purpose: every
 * transactions call makes the server compute a balance for its first row, which the board's
 * own focus-refetches already pay for once, and the rules do not change within a visit.
 *
 * The bank standing orders come from the accounting system's own list (the old board's `/debits`,
 * see bankStandingOrdersToFutureRows) — not from Nedarim — and only while their own flag is on
 * (HAT-1309): the list is read once per visit like the rest, and never when nothing will show it.
 */
const useFutureTransactionSources = (
  fundId: number | undefined,
  enabled: boolean,
  withBankStandingOrders: boolean = false,
  withSupportRequests: boolean = true
): FutureTransactionSources => {
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };
  const active = enabled && fundId !== undefined && Boolean(currentUser?.token);
  const bankActive = active && withBankStandingOrders;
  // Permission worlds (Druk, 22.10.2026): a coordinator closed to supports is not asked about their requests.
  const supportsActive = active && withSupportRequests;

  // No running balance for the look-ahead rows: the column is meaningless ahead of today, and computing it
  // cost the server a full balance run per board open that the rows then waited for.
  const aptUrl = `funds/financial-data/${fundId}/transactions?start=${getRelativeDate(1)}&end=${getRelativeDate(FUTURE_WINDOW_DAYS)}&runningBalance=false`;
  const apt = useQuery<Transaction[], Error>({
    queryKey: ['future-transactions', aptUrl],
    queryFn: ({ signal }) => getAuth<Transaction[]>(aptUrl, headers, undefined, signal),
    enabled: active,
    staleTime: FIVE_MINUTES,
  });

  const supportsUrl = `funds/${fundId}/support-requests?dateFrom=${SUPPORT_RANGE_START}&dateTo=${getRelativeDate(SUPPORT_RANGE_DAYS_AHEAD)}`;
  const supports = useQuery<SupportRequestDtoIn[], Error>({
    queryKey: ['future-transactions', supportsUrl],
    queryFn: ({ signal }) => getAuth<SupportRequestDtoIn[]>(supportsUrl, headers, undefined, signal),
    enabled: supportsActive,
    staleTime: FIVE_MINUTES,
  });

  const rulesUrl = `funds/financial-data/${fundId}/commission-rules`;
  const rules = useQuery<CommissionRules, Error>({
    queryKey: ['future-transactions', rulesUrl],
    queryFn: ({ signal }) => getAuth<CommissionRules>(rulesUrl, headers, undefined, signal),
    enabled: active,
    staleTime: FIVE_MINUTES,
  });

  const debitsUrl = `funds/financial-data/${fundId}/debits`;
  const debits = useQuery<Debit[], Error>({
    queryKey: ['future-transactions', debitsUrl],
    queryFn: ({ signal }) => getAuth<Debit[]>(debitsUrl, headers, undefined, signal),
    enabled: bankActive,
    staleTime: FIVE_MINUTES,
  });

  const failedSources: FutureTransactionSource[] = [];
  if (active && apt.isError) failedSources.push('aptTransactions');
  if (supportsActive && supports.isError) failedSources.push('supportRequests');
  if (active && rules.isError) failedSources.push('commissionRules');
  if (bankActive && debits.isError) failedSources.push('bankStandingOrders');

  return {
    aptTransactions: apt.data,
    supportRequests: supportsActive ? supports.data : undefined,
    commissionRules: rules.data,
    bankStandingOrders: bankActive ? debits.data : undefined,
    isLoading: (active && (apt.isLoading || rules.isLoading)) || (supportsActive && supports.isLoading) || (bankActive && debits.isLoading),
    failedSources,
    retryFailed: () => {
      if (apt.isError) void apt.refetch();
      if (supports.isError) void supports.refetch();
      if (rules.isError) void rules.refetch();
      if (debits.isError) void debits.refetch();
    },
  };
};

export default useFutureTransactionSources;
